// Feb-2026 · Auditoría externa PLD/FT — Bloque de autorización que vive en
// Configuración de la entidad (Configuración → Auditoría externa PLD/FT).
//
// Diseño:
// - Aclaración expresa: la autorización es TÉCNICA, no comercial (no contrata el servicio).
// - Switch para activar/revocar (solo admin_entidad; OC solo lee).
// - Al activar, diálogo con el ejercicio + inventario claro de lo que FELVORA podrá ver.
// - Estado vigente + bitácora COMPLETA (otorgar, revocar, generar, descargar, seguimiento).
import { useCallback, useEffect, useState } from "react";
import {
  ShieldCheck, ShieldOff, Loader2, ClipboardList,
  AlertTriangle, FileCheck2, Users, Bell, FileArchive, GraduationCap,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fdate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { DataTable } from "@/components/common/DataTable";

const anioActual = new Date().getFullYear();

// Íconos por tipo de acción de la bitácora
const ACC_ICON = {
  otorgar_autorizacion: <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />,
  revocar_autorizacion: <ShieldOff className="h-3.5 w-3.5 text-red-600" />,
  generar_paquete: <FileArchive className="h-3.5 w-3.5 text-sky-600" />,
  generar_informe: <ClipboardList className="h-3.5 w-3.5 text-indigo-600" />,
  descargar_paquete: <FileArchive className="h-3.5 w-3.5 text-slate-500" />,
  descargar_informe: <ClipboardList className="h-3.5 w-3.5 text-slate-500" />,
  seguimiento_informe: <FileCheck2 className="h-3.5 w-3.5 text-amber-600" />,
};

export default function AutorizacionAuditoriaExterna() {
  const { user } = useAuth();
  const entityId = user?.entity_id;
  const puedeAutorizar = user?.role === "admin_entidad";  // solo admin, no OC, no técnico

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autorizacion, setAutorizacion] = useState(null);
  const [bitacora, setBitacora] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [ejercicioInput, setEjercicioInput] = useState(String(anioActual));

  const cargar = useCallback(async () => {
    if (!entityId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/entidades/${entityId}/autorizacion-auditoria`),
        api.get(`/entidades/${entityId}/autorizacion-auditoria/bitacora`),
      ]);
      setAutorizacion(r1.data?.autorizacion || { activa: false });
      setBitacora(r2.data?.eventos || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [entityId]);
  useEffect(() => { cargar(); }, [cargar]);

  const abrirDialogoActivar = () => {
    setEjercicioInput(String(anioActual));
    setDialogOpen(true);
  };

  const confirmarActivar = async () => {
    const ej = Number(ejercicioInput);
    if (!Number.isInteger(ej) || ej < 2020 || ej > 2100) {
      toast.error("Ingresa un ejercicio válido (año, ej. 2026).");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/entidades/${entityId}/autorizacion-auditoria`, { ejercicio: ej });
      toast.success(`Autorización otorgada para el ejercicio ${ej}.`);
      setDialogOpen(false);
      await cargar();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const confirmarRevocar = async () => {
    if (!confirm("¿Revocar la autorización? Se cerrará la generación de nuevos documentos. Los documentos ya emitidos se conservan (retención regulatoria).")) return;
    setSaving(true);
    try {
      await api.delete(`/entidades/${entityId}/autorizacion-auditoria`);
      toast.success("Autorización revocada.");
      await cargar();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Cargando…</div>;

  const activa = !!autorizacion?.activa;

  return (
    <div className="space-y-5" data-testid="autorizacion-auditoria-externa">

      {/* Aviso: servicio adicional · autorización técnica, no comercial */}
      <Alert className="border-amber-300 bg-amber-50">
        <AlertTriangle className="h-4 w-4 text-amber-700" />
        <AlertDescription className="text-xs text-amber-900">
          <strong>Servicio adicional de FELVORA.</strong> La auditoría anual la practica un auditor externo
          independiente conforme a la <strong>Disposición 52ª</strong>. FELVORA puede prestarlo como servicio.
          Activar este interruptor es una <strong>autorización TÉCNICA</strong> (permite el acceso a la información
          para auditar), <strong>no un acto de contratación</strong>. La contratación se acuerda por separado con FELVORA.
        </AlertDescription>
      </Alert>

      {/* Descripción normativa */}
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 text-xs text-slate-600">
        <p>
          <strong className="text-slate-800">Auditoría externa PLD/FT.</strong> Al autorizar, FELVORA podrá generar,
          desde su panel de plataforma y para el ejercicio elegido, el <strong>Paquete de evidencia CNBV (43ª-II)</strong> y
          el <strong>Informe de Auditoría .docx (52ª)</strong> con los datos de tu entidad. Un ejercicio a la vez.
          La autorización se puede revocar en cualquier momento; los documentos ya emitidos se conservan por
          <strong> 5 años</strong> (retención regulatoria).
        </p>
      </div>

      {/* Estado + interruptor */}
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <h3 className="mb-2 flex items-center gap-2 font-heading text-base font-semibold text-slate-900">
          {activa ? <ShieldCheck className="h-4 w-4 text-emerald-600" /> : <ShieldOff className="h-4 w-4 text-slate-400" />}
          Estado de la autorización
        </h3>

        {activa ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 text-sm text-emerald-900" data-testid="autorizacion-vigente">
            <p>
              <strong>Autorización vigente para el ejercicio {autorizacion.ejercicio}.</strong>{" "}
              Otorgada por <strong>{autorizacion.otorgada_por_nombre}</strong> el <strong>{fdate(autorizacion.otorgada_at)}</strong>.
            </p>
          </div>
        ) : (
          <p className="text-sm text-slate-500" data-testid="autorizacion-inactiva">
            {autorizacion?.revocada_at ? (
              <>Autorización <strong>revocada</strong> el {fdate(autorizacion.revocada_at)} por {autorizacion.revocada_por_nombre}. Puedes volver a otorgarla cuando lo requieras.</>
            ) : (
              <>Sin autorización otorgada. FELVORA no puede acceder a los datos para auditar.</>
            )}
          </p>
        )}

        <div className="mt-4 flex items-center gap-3">
          <Switch
            data-testid="autorizacion-switch"
            checked={activa}
            disabled={!puedeAutorizar || saving}
            onCheckedChange={(v) => { if (v) abrirDialogoActivar(); else confirmarRevocar(); }}
          />
          <Label className="text-sm">
            Autorizar a FELVORA como auditor externo
          </Label>
          {!puedeAutorizar && (
            <Badge variant="outline" className="text-[10.5px] text-slate-500">
              Solo el administrador de la entidad puede activar o revocar.
            </Badge>
          )}
        </div>
      </div>

      {/* Bitácora completa (otorgar, revocar, generar, descargar, seguimiento) */}
      <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="bitacora-auditoria-felvora">
        <h3 className="mb-2 flex items-center gap-2 font-heading text-base font-semibold text-slate-900">
          <ClipboardList className="h-4 w-4 text-primary" /> Bitácora de auditoría externa
        </h3>
        <p className="mb-3 text-xs text-slate-500">
          Registro completo y no editable de cada acción sobre esta autorización: quién la otorgó,
          quién la revocó, cuándo FELVORA generó cada paquete o informe, y sus sellos SHA-256.
        </p>
        <DataTable
          testid="table-bitacora-felvora"
          rows={bitacora}
          columns={[
            { key: "at", label: "Fecha", render: (r) => (
                <span className="text-xs">{fdate(r.at)} <span className="text-slate-400">{String(r.at || "").slice(11, 16)}</span></span>
              ) },
            { key: "accion", label: "Acción", render: (r) => (
                <span className="inline-flex items-center gap-1.5 text-xs" data-testid={`bitacora-accion-${r.accion}`}>
                  {ACC_ICON[r.accion] || <FileCheck2 className="h-3.5 w-3.5 text-slate-400" />}
                  {r.accion_label || r.accion}
                </span>
              ) },
            { key: "ejercicio", label: "Ejercicio",
              render: (r) => r.ejercicio ? <Badge variant="outline" className="font-mono text-[10.5px]">{r.ejercicio}</Badge> : <span className="text-slate-300">—</span> },
            { key: "por_nombre", label: "Quién",
              render: (r) => (
                <span className="text-xs">
                  <span className="font-medium text-slate-700">{r.por_nombre}</span>
                  <span className="ml-1 text-[10.5px] text-slate-400">({r.por_rol})</span>
                </span>
              ) },
            { key: "sello_sha256", label: "Sello",
              render: (r) => r.sello_sha256
                ? <Badge variant="outline" className="font-mono text-[10px]" title={r.sello_sha256}>{r.sello_sha256.slice(0, 12)}…</Badge>
                : <span className="text-slate-300">—</span> },
            { key: "descripcion", label: "Detalle",
              render: (r) => <span className="text-[11px] text-slate-500">{r.descripcion || "—"}</span> },
          ]}
          emptyLabel="Sin eventos registrados. Cuando otorgues autorización o FELVORA genere un documento, aparecerá aquí."
        />
      </div>

      {/* Diálogo de activación con inventario de lo que FELVORA verá */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="dialog-activar-auditoria">
          <DialogHeader>
            <DialogTitle>Autorizar a FELVORA como auditor externo</DialogTitle>
            <DialogDescription>
              Un ejercicio a la vez. La autorización es técnica y se puede revocar en cualquier momento.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label htmlFor="ejercicio-input">Ejercicio a auditar</Label>
              <Input
                id="ejercicio-input"
                data-testid="ejercicio-input"
                type="number" min="2020" max="2100"
                value={ejercicioInput}
                onChange={(e) => setEjercicioInput(e.target.value)}
              />
              <p className="mt-1 text-[11px] text-slate-500">
                Por lo general, el ejercicio auditado es el año concluido (cierre 31-dic).
              </p>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700" data-testid="inventario-acceso">
              <p className="mb-2 font-semibold text-slate-800">Al activarlo, FELVORA podrá consultar de tu entidad:</p>
              <ul className="space-y-1.5">
                <li className="flex items-center gap-2"><Users className="h-3.5 w-3.5 text-slate-500" /> Expedientes KYC y su muestreo</li>
                <li className="flex items-center gap-2"><Bell className="h-3.5 w-3.5 text-slate-500" /> Alertas PLD/FT y su seguimiento</li>
                <li className="flex items-center gap-2"><FileCheck2 className="h-3.5 w-3.5 text-slate-500" /> Casos y dictámenes del OC</li>
                <li className="flex items-center gap-2"><FileArchive className="h-3.5 w-3.5 text-slate-500" /> Reportes UIF (relevantes, inusuales, 24 h, preocupantes) y sus acuses</li>
                <li className="flex items-center gap-2"><GraduationCap className="h-3.5 w-3.5 text-slate-500" /> Capacitación PLD/FT del personal (52ª A-VI)</li>
                <li className="flex items-center gap-2"><ClipboardList className="h-3.5 w-3.5 text-slate-500" /> EBR institucional, perfil transaccional y calendario de plazos</li>
              </ul>
              <p className="mt-2 text-[10.5px] text-slate-500">
                Las <strong>denuncias del canal interno</strong> se comparten solo como métrica agregada
                (sin identidad del denunciante ni detalle) por confidencialidad regulatoria (43ª-VIII).
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button
              className="bg-forest-600 hover:bg-forest-700"
              data-testid="confirmar-activar-btn"
              disabled={saving}
              onClick={confirmarActivar}
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
              Autorizar para el ejercicio {ejercicioInput}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
